import { TriwordStore } from './TriwordStore';
import { SpecialKeys } from '../shared/utils';
import { PuzzleNames, STATS_DIALOG_DELAY, TriwordData } from '../../../utils/types';
import { savePuzzleProgress } from '../../../utils/persistenceUtils';

export const onReveal = () => {
  TriwordStore.update(s => {
    clearInterval(s.timerUpdatingFn);
    s.revealed = true;
    s.isPaused = false;

    savePuzzleProgress(PuzzleNames.Triword, {
      puzzleId: s.puzzleId,
      revealed: true,
      solved: false,
      time: s.time,
    });
  });
};

export const onSolve = () => {
  const { time, puzzleId } = TriwordStore.getRawState();

  // the other puzzles don't call this inside onSolve, only triword does. i don't love that.
  TriwordStore.update(s => {
    s.solved = true;
  });

  setTimeout(() => {
    TriwordStore.update(s => {
      s.statsDialogIsOpen = true;
    });
  }, STATS_DIALOG_DELAY);

  savePuzzleProgress(PuzzleNames.Triword, {
    puzzleId,
    revealed: false,
    solved: true,
    time,
  });
};

export const saveTriword = () => {
  const { solved, revealed, time, used5050Lifeline, usedLetterLifeline,
    usedPositionLifeline, puzzleId } = TriwordStore.getRawState();

  // this fn is just for updating progress on pause or navigate away.
  if (solved || revealed) return;

  savePuzzleProgress(PuzzleNames.Triword, {
    puzzleId,
    revealed,
    solved,
    time,
    partialState: {
      used5050Lifeline,
      usedLetterLifeline,
      usedPositionLifeline,
    },
  });
};

export const handleInput = (key: string) => {
  const {
    guess, solution, solved, revealed, numInputs,
    usedLetterLifeline, obscureSolutions,
  } = TriwordStore.getRawState();

  if (solved || revealed) return;

  if (key === SpecialKeys.Delete) {
    TriwordStore.update(s => {
      s.guess = guess.slice(0, -1);
    });
    return;
  }

  if (/^[a-zA-Z]$/.test(key)) {
    key = key.toLowerCase();

    const noRoomForInput = guess.length === numInputs
      || (usedLetterLifeline && guess.length === numInputs - 1);

    if (noRoomForInput) {
      return;
    }

    const lastLetter = solution[solution.length - 1];
    const newGuess = usedLetterLifeline ?
      guess.join('') + key + lastLetter : guess.join('') + key;

    TriwordStore.update(s => {
      s.guess = [...s.guess, key];
    });

    if (newGuess === solution) {
      onSolve();
    } else if (obscureSolutions.includes(newGuess) && (newGuess.length === solution.length)) {
      // Some of the obscure solutions are 3-letter solutions for 4-letter puzzles,
      // so we do a length check since we don't want to trigger this alert for those
      alert("Nice work! This solution is valid, but at least one of the words is extremely rare. A less obscure solution exists.");
    } else if (newGuess.length === solution.length) {
      TriwordStore.update(s => { s.wrongGuess = true; });
      setTimeout(() => {
        TriwordStore.update(s => { s.wrongGuess = false; });
      }, 300);
    }
  }
};

export const useLetterLifeline = () => {
  // if they currently have the last letter filled with a guess,
  // clear that slot before replacing it with the revealed letter
  const { guess, solution } = TriwordStore.getRawState();
  if (guess.length === solution.length) {
    TriwordStore.update(s => {
      s.guess = s.guess.slice(0, s.guess.length - 1);
    });
  }
};

export const initTriword = (puzzleData: TriwordData) => {
  // puzzleData.clues has the full words, and we then slice the solution out of them
  // before displaying the remaining fragments to the user
  const fragments = puzzleData.clues.map((fullWord, index) => {
    return fullWord.replace(puzzleData.solution, '');
  });

  const fragmentsWith1PositionRevealed = puzzleData.clues.map((fullWord, index) => {
    if (fullWord === puzzleData.positionLifeline) {
      return fullWord.replace(puzzleData.solution, '_'.repeat(puzzleData.solution.length));
    }
    return fullWord.replace(puzzleData.solution, '');
  });

  const obscureSolutions = puzzleData.obscureSolutions || [];

  TriwordStore.update(s => {
    s.puzzleId = puzzleData.puzzleId;
    s.solution = puzzleData.solution;
    s.fullWords = puzzleData.clues;
    s.fragments = fragments;
    s.fragmentsWith1PositionRevealed = fragmentsWith1PositionRevealed;
    s.obscureSolutions = obscureSolutions;
    s.numInputs = puzzleData.solution.length;
    s.lettersFor5050 = puzzleData.lettersFor5050;
  });
};